<template>
  <div class="mx-n3">
    <transition name="grow">
      <div v-if="progressVisible && (gen.active || gen.done > 0)">
        <div class="frosted p-3">
          {{ progressLabel }}
          {{ gen.done }}/{{ gen.total }}
          <span v-if="gen.errors.length" class="text-warning ml-2">
            ({{ gen.errors.length }} failed)
          </span>
          <div class="d-flex align-items-center gap-3">
            <b-progress :animated="gen.active" :value="gen.done" :max="gen.total" class="flex-1"/>
            <b-button
              :disabled="!gen.active || gen.cancelled || gen.phase === 'saving'"
              size="sm"
              variant="outline-secondary"
              class="ml-2 py-0"
              @click="cancel"
            >Cancel</b-button>
          </div>
        </div>
      </div>
    </transition>

    <div
      v-if="currentProps"
      style="position:fixed;left:-9999px;width:200px;height:200px;visibility:hidden;pointer-events:none"
      aria-hidden="true"
    >
      <FrameRenderer
        v-if="currentProps"
        ref="renderer"
        v-bind="currentProps"
        :interactive="false"
        :wall-enabled="false"
        :decor-enabled="false"
        :awaiting-config="false"
      />
    </div>
  </div>
</template>

<script>
import FrameRenderer from '@/components/FrameRenderer.vue'
import { http2 } from '@/services/http.js'
import { buildRendererPropsFromItem } from '@/utils/dsItemRendererProps.js'
import { stableSceneStringify } from '@/utils/designStudioSceneSession.js'
import { bulkUpdateItems } from '@/utils/itemEditSessionSave.js'

const THUMBNAIL_SAVE_BATCH_SIZE = 10
const THUMBNAIL_SAVE_CONCURRENCY = 4

export function createTaskQueue(concurrency = 1) {
  const pending = []
  let active = 0

  const drain = () => {
    while (active < concurrency && pending.length) {
      const { task, resolve, reject } = pending.shift()
      active++
      Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
          active--
          drain()
        })
    }
  }

  return {
    add(task) {
      return new Promise((resolve, reject) => {
        pending.push({ task, resolve, reject })
        drain()
      })
    },
  }
}

export default {
  name: 'ProjectThumbnailGenerator',
  components: { FrameRenderer },
  props: {
    showProgress: { type: Boolean, default: true },
  },

  data() {
    return {
      gen: {
        active: false,
        blocking: false,
        total: 0,
        done: 0,
        saveDone: 0,
        errors: [],
        cancelled: false,
        phase: 'idle',
        showProgress: false,
      },
      currentProps: null,
    }
  },

  computed: {
    active() { return this.gen.active },
    blocking() { return this.gen.active && this.gen.blocking },
    progressVisible() { return this.showProgress || this.gen.showProgress },
    progressLabel() {
      if (!this.gen.active) return 'Thumbnails generated'
      return this.gen.phase === 'saving' ? 'Saving thumbnails\u2026' : 'Generating thumbnails\u2026'
    },
  },

  mounted() {
    this.prepared = new Map()
    this._onBeforeUnload = (e) => {
      if (this.blocking) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this._onBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this._onBeforeUnload)
    this.gen.cancelled = true  // stop the loop without showing a toast
    this._destroyed = true
  },

  methods: {
    cancel() {
      this.gen.cancelled = true
    },

    cacheKey(item) {
      if (!item) return ''
      if (item.id != null) return `id:${item.id}`
      return `new:${item.item_sort ?? ''}:${item.imagedata?.id ?? item.imagedata?.local_id ?? ''}`
    },

    renderHash(item) {
      if (!item) return ''
      const configData = { ...(item.config_data || {}) }
      // Project-wall coordinates do not affect this component's isolated item
      // render. Excluding them prevents collision reflow from regenerating a
      // thumbnail for every neighbor after one item is resized.
      delete configData.wall_offset_x
      delete configData.wall_offset_y
      return stableSceneStringify({
        imagedata: item.imagedata || null,
        width: item.width ?? null,
        height: item.height ?? null,
        config_data: configData,
      })
    },

    shouldPrepare(item, committedItems = []) {
      if (!item?.imagedata) return false
      if (item.id == null) return true
      const committed = committedItems.find(committedItem => String(committedItem.id) === String(item.id))
      if (!committed) return true
      return !item.thumbnail || this.renderHash(item) !== this.renderHash(committed)
    },

    preparedThumbnailFor(item) {
      const key = this.cacheKey(item)
      const cached = key ? this.prepared.get(key) : null
      return cached?.hash === this.renderHash(item) ? cached.thumbnail || null : null
    },

    async prepare(items, { committedItems = [] } = {}) {
      const pending = (items || [])
        .filter(item => item && !item.deleted_at && this.shouldPrepare(item, committedItems))
        .map(item => ({
          item,
          key: this.cacheKey(item),
          hash: this.renderHash(item),
        }))
        .filter(entry => {
          if (!entry.key || !entry.hash) return false
          const cached = this.prepared.get(entry.key)
          return !cached || cached.hash !== entry.hash || !cached.thumbnail
        })

      if (!pending.length) return []
      if (this.active) {
        await this._activeRun
      }

      const results = await this.generate(pending.map(entry => entry.item), { persist: false }) || []
      results.forEach((result) => {
        const entry = pending[result?.sourceIndex]
        if (!entry || !result?.thumbnail) return
        this.prepared.set(entry.key, {
          hash: entry.hash,
          thumbnail: result.thumbnail,
        })
      })
      return results
    },

    async start(items, options = {}) {
      return this.generate(items, { persist: true, ...options })
    },

    async generate(items, { persist = false, showProgress = false } = {}) {
      if (!items?.length) return
      if (this.gen.active) {
        return this._activeRun || []
      }
      this._activeRun = this._generate(items, { persist, showProgress })
      try {
        return await this._activeRun
      } finally {
        this._activeRun = null
      }
    },

    async _generate(items, { persist = false, showProgress = false } = {}) {
      this.gen = {
        active: true,
        blocking: persist,
        total: items.length,
        done: 0,
        saveDone: 0,
        errors: [],
        cancelled: false,
        phase: 'generating',
        showProgress,
      }
      this._destroyed = false
      const results = []
      const thumbnailPool = []
      const savePromises = []
      const generationQueue = createTaskQueue(1)
      const saveQueue = createTaskQueue(THUMBNAIL_SAVE_CONCURRENCY)

      const saveBatch = async (batch) => {
        try {
          const response = await bulkUpdateItems(
            http2,
            batch.map(({ id, thumbnail }) => ({ id, thumbnail })),
          )
          const sourceIndexById = new Map(batch.map(result => [String(result.id), result.sourceIndex]))
          const rowErrors = response.data?.errors || []
          rowErrors.forEach((row) => {
            this.gen.errors.push({
              id: row.id,
              error: row.error || JSON.stringify(row.errors || {}),
            })
          })
          const savedBatch = (response.data?.items || [])
            .filter(item => item?.thumbnail)
            .map(item => ({
              id: item.id,
              thumbnail: item.thumbnail,
              sourceIndex: sourceIndexById.get(String(item.id)),
            }))
          results.push(...savedBatch)
          if (!this._destroyed) {
            savedBatch.forEach(({ id, thumbnail }) => {
              this.$emit('thumbnail-saved', { id, thumbnail })
            })
          }
        } catch (err) {
          batch.forEach(({ id }) => {
            this.gen.errors.push({ id, error: err?.message })
          })
        } finally {
          this.gen.saveDone += batch.length
        }
      }

      const queueSaveBatch = () => {
        const batch = thumbnailPool.splice(0, THUMBNAIL_SAVE_BATCH_SIZE)
        if (batch.length) {
          savePromises.push(saveQueue.add(() => saveBatch(batch)))
        }
      }

      const generateThumbnail = async (item, sourceIndex) => {
        if (this.gen.cancelled) return
        try {
          this.currentProps = buildRendererPropsFromItem(item)
          await this.$nextTick()
          const thumbnail = await this.$refs.renderer?.generateThumbnail(800)
          if (thumbnail) {
            const generated = { id: item.id, thumbnail, sourceIndex }
            if (persist) {
              thumbnailPool.push(generated)
              if (thumbnailPool.length >= THUMBNAIL_SAVE_BATCH_SIZE) queueSaveBatch()
            } else {
              results.push(generated)
            }
          } else if (persist) {
            this.gen.saveDone++
          }
        } catch (err) {
          this.gen.errors.push({ id: item.id, error: err?.message })
          if (persist) this.gen.saveDone++
        } finally {
          this.gen.done++
        }
      }

      const generationPromises = items.map((item, sourceIndex) => (
        generationQueue.add(() => generateThumbnail(item, sourceIndex))
      ))
      await Promise.all(generationPromises)

      if (persist && thumbnailPool.length && !this._destroyed) queueSaveBatch()
      if (savePromises.length) {
        this.gen.phase = 'saving'
        await Promise.all(savePromises)
        results.sort((a, b) => a.sourceIndex - b.sourceIndex)
      }

      // Keep the hidden renderer alive between batches. Reusing its scene and
      // WebGL context avoids allocating one THREE.Scene per staged edit; the
      // component's existing beforeDestroy cleanup still runs with the page.
      this.gen.active = false
      this.gen.phase = 'done'

      if (this._destroyed) return results

      setTimeout(() => { this.gen.done = 0 }, 4000)
      return results
    },
  },
}
</script>

<style scoped lang="scss">
  .grow-enter-active,
  .grow-leave-active {
    overflow: hidden;
    transition: all .4s ease;
    max-height: 80px;
  }
  .grow-enter, .grow-leave-to {
    max-height: 0;
  }
</style>
